import React, { useMemo } from 'react';
import { DesignTokens, GivelifyButton, GivelifyIcon } from '@givelify/ui';
import { useMediaQuery, useTheme } from '@mui/material';
import { Envelope } from 'api/models';
import { ListProps, ItemProps, Virtuoso } from 'react-virtuoso';
import { useAdvancedTranslation } from 'utils/i18n';
import EnvelopeMobileSortItem from '../../../components/EnvelopeListItem/EnvelopeMobileSortItem';
import { useEnvelopesContext } from '../../../context/EnvelopesProvider';
import {
    CloseModal,
    Content,
    DoneButton,
    DragDescription,
    ModalHeader,
    ModalMobileTitle,
    SortModal,
    SortModalFooter,
} from './styles';

interface MobileSortModalProps {
    open: boolean;
    onClose: () => void;
    list: React.ComponentType<ListProps>;
    item: React.ComponentType<ItemProps<Envelope>>;
}

const MobileSortModal: React.FCC<MobileSortModalProps> = ({
    open,
    onClose,
    item,
    list,
}) => {
    const theme = useTheme();
    const isMobile = useMediaQuery(theme.breakpoints.down('mobile'));
    const { at, t } = useAdvancedTranslation();
    const copy = useMemo(
        () => ({
            reorderEnvelope: t('pages.settings.envelopes2.reorderEnvelope'),
            dragDescription: at('pages.settings.envelopes2.dragDescription'),
        }),
        [at, t],
    );

    const { activeEnvelopes: items, updatePrioritiesRequestState } =
        useEnvelopesContext();

    return (
        <SortModal
            fullWidth
            id="reorderModal"
            name="sortModal"
            onClose={onClose}
            open={open}
        >
            {isMobile && (
                <ModalHeader>
                    <CloseModal>
                        <GivelifyButton onClick={onClose} variant="icon">
                            <GivelifyIcon variant="close-circle" />
                        </GivelifyButton>
                    </CloseModal>
                    <ModalMobileTitle
                        fontWeight={800}
                        text={copy.reorderEnvelope}
                    />
                </ModalHeader>
            )}
            <Content>
                <DragDescription
                    color={DesignTokens.color.textSecondary}
                    marginBottom={3}
                    text={copy.dragDescription}
                    variant="body1"
                />
                <Virtuoso
                    className="list"
                    components={{
                        List: list,
                        Item: item,
                    }}
                    data={items}
                    itemContent={(index, item) => (
                        <EnvelopeMobileSortItem key={item.id} {...item} />
                    )}
                    style={{
                        width: '100%',
                        height: 'calc(100vh - 240px)',
                    }}
                />
            </Content>
            <SortModalFooter>
                <DoneButton
                    isLoading={updatePrioritiesRequestState === 'REQUEST_START'}
                    onClick={onClose}
                    text="Done"
                    variant="primary"
                />
            </SortModalFooter>
        </SortModal>
    );
};

export default MobileSortModal;
